import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, Link, useLocation } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';
import RMProFormaDashboard from './pages/RMProForma_Dashboard';
import MortgageLOS from './pages/MortgageLOS'; // Import the new MortgageLOS component
import RegionalForecastingModule from './pages/RegionalForecastingModule';
import ForecastCycleHome from './pages/forecasting/ForecastCycleHome';
import ForecastReviewConsole from './pages/forecasting/ForecastReviewConsole';
import ForecastReviewDetail from './pages/forecasting/ForecastReviewDetail';
import ForecastStatements from './pages/forecasting/ForecastStatements';
import ForecastProjects from './pages/forecasting/ForecastProjects';
import ForecastProjectWorkspace from './pages/forecasting/ForecastProjectWorkspace';
import ForecastExecView from './pages/forecasting/ForecastExecView';
import ForecastGuide from './pages/forecasting/ForecastGuide';
import ForecastMacroScenarios from './pages/forecasting/ForecastMacroScenarios';
import CloseIntelHome from './modules/close-intel/CloseIntelHome';
import { ReactComponent as AceDiamondSparkIcon } from './ace-icon-17-ace-diamond-spark.svg';

const Navigation = () => {
  const location = useLocation();
  const navItems = [
    { to: '/forecasting', label: 'Forecasting' },
    { to: '/forecasting/guide', label: 'Guide' },
    { to: '/rm-pro-forma', label: 'RM Pro Forma' },
    { to: '/mortgage-los', label: 'Mortgage LOS' }
  ];

  const isActive = (path) => {
    if (path === '/forecasting') {
      return location.pathname.startsWith('/forecasting') && !location.pathname.startsWith('/forecasting/guide');
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="ace-nav" aria-label="Primary">
      {navItems.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className={`ace-nav-link ${isActive(item.to) ? 'on' : ''}`}
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
};

const AppShell = () => {
  const [direction, setDirection] = useState(() => {
    if (typeof window === 'undefined') return 'B';
    return window.localStorage.getItem('ace-direction') === 'A' ? 'A' : 'B';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-direction', direction);
    document.body.classList.add('ace-hub-theme-active');
    document.body.setAttribute('data-direction', direction);
    window.localStorage.setItem('ace-direction', direction);
  }, [direction]);

  useEffect(() => {
    const syncDirection = (event) => {
      if (event.key !== 'ace-direction') return;
      if (event.newValue === 'A' || event.newValue === 'B') {
        setDirection(event.newValue);
      }
    };

    window.addEventListener('storage', syncDirection);
    return () => window.removeEventListener('storage', syncDirection);
  }, []);

  return (
    <div className="App min-h-screen ace-hub-theme" data-direction={direction}>
      <header className="ace-header">
        <div className="ace-header-wrap">
          <Link to="/forecasting" className="ace-brand">
            <AceDiamondSparkIcon className="ace-brand-mark" aria-hidden="true" focusable="false" />
            <span className="ace-brand-copy">
              <span className="ace-brand-title">Bank Analysis</span>
              <span className="ace-brand-sub">aceanalytics.dev / parallax</span>
            </span>
          </Link>

          <Navigation />

          <div className="ace-header-controls">
            <div className="ace-theme-toggle" role="group" aria-label="Theme toggle">
              <button
                type="button"
                className={direction === 'A' ? 'on' : ''}
                onClick={() => setDirection('A')}
                aria-pressed={direction === 'A'}
                aria-label="Use light theme"
                title="Light mode"
              >
                <Sun size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={direction === 'B' ? 'on' : ''}
                onClick={() => setDirection('B')}
                aria-pressed={direction === 'B'}
                aria-label="Use dark theme"
                title="Dark mode"
              >
                <Moon size={14} aria-hidden="true" />
              </button>
            </div>
            <a href="https://aceanalytics.dev" className="ace-header-pill">
              ACE Home
            </a>
          </div>
        </div>
      </header>

      <main className="ace-main">
        <div className="ace-main-wrap">
          <Routes>
            <Route path="/" element={<Navigate to="/forecasting" replace />} />
            <Route path="/forecasting" element={<ForecastCycleHome />} />
            <Route path="/forecasting/input/:submissionId" element={<RegionalForecastingModule />} />
            <Route path="/forecasting/review" element={<ForecastReviewConsole />} />
            <Route path="/forecasting/statements" element={<ForecastStatements />} />
            <Route path="/forecasting/review/:submissionId" element={<ForecastReviewDetail />} />
            <Route path="/forecasting/exec" element={<ForecastExecView />} />
            <Route path="/forecasting/macro" element={<ForecastMacroScenarios />} />
            <Route path="/forecasting/close-intel" element={<CloseIntelHome />} />
            <Route path="/forecasting/guide" element={<ForecastGuide />} />
            <Route path="/forecasting/projects" element={<ForecastProjects />} />
            <Route path="/forecasting/projects/:projectId" element={<ForecastProjectWorkspace />} />
            <Route path="/rm-pro-forma" element={<RMProFormaDashboard />} />
            <Route path="/mortgage-los" element={<MortgageLOS />} />
            <Route
              path="/forecasting/*"
              element={<Navigate to="/forecasting" replace state={{ routeNotice: 'Unknown forecasting page — redirected to cycle home.' }} />}
            />
            <Route
              path="*"
              element={<Navigate to="/forecasting" replace state={{ routeNotice: 'Unknown page — redirected to forecasting home.' }} />}
            />
          </Routes>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
